import React from "react";

// Détail journalier des opérations de production VT — tableau large imprimable.
// data = lignes journalières (DTO backend) ; mois = "YYYY-MM".

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet",
    "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const fmt = (v) => (v != null && v !== 0 ? Number(v).toLocaleString("fr-FR") : (v === 0 ? "0" : "-"));
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "");

// Groupes (label, base de commission, prix unitaire, clés DTO nombre/montant)
const GROUPS = [
    { label: "VOITURE", base: 12000, prix: 13000, nb: "nbVoiture", mt: "mtVoiture" },
    { label: "CAMION", base: 18500, prix: 19500, nb: "nbCamion", mt: "mtCamion" },
    { label: "TOTAL", base: null, prix: null, nb: "nbTotalAn", mt: "mtTotalAn" },
    { label: "PROROGATION VL 7 JOURS", base: 6735, prix: 7735, nb: "nbProrogVl7", mt: "mtProrogVl7" },
    { label: "PROROGATION VL 15 JOURS", base: 12000, prix: 13000, nb: "nbProrogVl15", mt: "mtProrogVl15" },
    { label: "PROROGATION CAMION 7 JOURS", base: 9765, prix: 10765, nb: "nbProrogCam7", mt: "mtProrogCam7" },
    { label: "PROROGATION CAMION 15 JOURS", base: 18500, prix: 19500, nb: "nbProrogCam15", mt: "mtProrogCam15" },
    { label: "CP VL PLAQUÉS", base: 5000, prix: 6000, nb: "nbCpPlaque", mt: "mtCpPlaque" },
    { label: "CP VL NON PLAQUÉS", base: 0, prix: 3000, nb: "nbCpNonPlaque", mt: "mtCpNonPlaque" },
    { label: "RENOUVEL VL", base: 12000, prix: 13000, nb: "nbRenouvelVl", mt: "mtRenouvelVl" },
    { label: "RENOUVEL CAMION", base: 18500, prix: 19500, nb: "nbRenouvelCam", mt: "mtRenouvelCam" },
    { label: "DUPLICATA", base: 0, prix: 1500, nb: "nbDuplicata", mt: "mtDuplicata" },
];

const SINGLE = [
    { label: "TOTAL ENCAISSÉE", key: "totalEncaisse" },
    { label: "Base de commission STAC", key: "baseCommissionStac" },
    { label: "REPORT VEILLE", key: "reportVeille" },
    { label: "VERSEMENT DU JOUR (hors timbre)", key: "versementDuJour" },
    { label: "TIMBRE", key: "timbre" },
    { label: "SOLDE À REVERSER", key: "soldeAReverser" },
];

const EtatProductionVT = React.forwardRef(({ data = [], mois = "" }, ref) => {
    const [year, m] = (mois || "").split("-");
    const moisLabel = m ? `${MONTHS_FR[parseInt(m, 10) - 1]?.toUpperCase()} ${year}` : "";

    // Totaux pied de tableau
    const totals = {};
    GROUPS.forEach((g) => {
        totals[g.nb] = data.reduce((s, r) => s + (Number(r[g.nb]) || 0), 0);
        totals[g.mt] = data.reduce((s, r) => s + (Number(r[g.mt]) || 0), 0);
    });
    SINGLE.forEach((c) => { totals[c.key] = data.reduce((s, r) => s + (Number(r[c.key]) || 0), 0); });

    const cell = { border: "1px solid #333", padding: "2px 4px", textAlign: "right", whiteSpace: "nowrap" };
    const head = { ...cell, textAlign: "center", background: "#f0f0f0", fontWeight: 600 };
    const red = { color: "#c00", fontWeight: 700 };

    return (
        <div ref={ref} style={{ fontFamily: "Arial, sans-serif", fontSize: 9, color: "#111" }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 2 }}>DÉTAIL JOURNALIER DES OPÉRATIONS DE PRODUCTION VT</div>
            <div style={{ marginBottom: 8 }}>MOIS DE <b>{moisLabel}</b></div>

            <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                    {/* Ligne 1 : libellés de groupes */}
                    <tr>
                        <th style={head} rowSpan={3}>DATE</th>
                        {GROUPS.map((g) => (
                            <th key={g.label} style={head} colSpan={2}>{g.label}</th>
                        ))}
                        {SINGLE.map((c) => (
                            <th key={c.key} style={head} rowSpan={3}>{c.label}</th>
                        ))}
                    </tr>
                    {/* Ligne 2 : base de calcul commission */}
                    <tr>
                        {GROUPS.map((g) => (
                            <React.Fragment key={g.label + "-base"}>
                                <th style={head}>Base de calcul commission</th>
                                <th style={{ ...head, ...red }}>{g.base != null ? fmt(g.base) : ""}</th>
                            </React.Fragment>
                        ))}
                    </tr>
                    {/* Ligne 3 : NOMBRE / prix unitaire */}
                    <tr>
                        {GROUPS.map((g) => (
                            <React.Fragment key={g.label + "-nb"}>
                                <th style={head}>NOMBRE</th>
                                <th style={{ ...head, ...red }}>{g.prix != null ? fmt(g.prix) : "MONTANT"}</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((r, i) => (
                        <tr key={i}>
                            <td style={{ ...cell, textAlign: "center" }}>{fmtDate(r.date)}</td>
                            {GROUPS.map((g) => (
                                <React.Fragment key={g.label + i}>
                                    <td style={cell}>{Number(r[g.nb]) || 0}</td>
                                    <td style={cell}>{fmt(r[g.mt])}</td>
                                </React.Fragment>
                            ))}
                            {SINGLE.map((c) => (
                                <td key={c.key + i} style={cell}>{fmt(r[c.key])}</td>
                            ))}
                        </tr>
                    ))}
                    {/* Ligne TOTAL */}
                    <tr style={{ fontWeight: 700, background: "#f6f6f6" }}>
                        <td style={{ ...cell, textAlign: "center" }}>TOTAL</td>
                        {GROUPS.map((g) => (
                            <React.Fragment key={g.label + "-tot"}>
                                <td style={cell}>{totals[g.nb]}</td>
                                <td style={cell}>{fmt(totals[g.mt])}</td>
                            </React.Fragment>
                        ))}
                        {SINGLE.map((c) => (
                            <td key={c.key + "-tot"} style={cell}>{fmt(totals[c.key])}</td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
});

EtatProductionVT.displayName = "EtatProductionVT";

export default EtatProductionVT;
